import React, { useState, useEffect, useRef } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { motion, AnimatePresence } from "framer-motion";
import { MdCheck } from "react-icons/md";

interface CollectionGenerationLoaderProps {
  isGenerating: boolean;
  isDarkMode: boolean;
  onComplete?: () => void;
}

interface LoadingStage {
  id: number;
  title: string;
  description: string;
  lottieFile: string;
  duration: number;
}

// Only 3 processing stages - no success stage in loader
const STAGES: LoadingStage[] = [
  {
    id: 1,
    title: "AI Selecting Categories",
    description:
      "Analyzing your theme and description to intelligently select the best categories...",
    lottieFile: "/assets/lottie_files/Grouping.lottie",
    duration: 6,
  },
  {
    id: 2,
    title: "Finding Perfect Matches",
    description: "Scanning through products to find the best fit...",
    lottieFile: "/assets/lottie_files/Searching.lottie",
    duration: 8,
  },
  {
    id: 3,
    title: "Crafting Your Collection",
    description: "AI is curating a stunning collections...",
    lottieFile: "/assets/lottie_files/Treasure.lottie",
    duration: 5,
  },
];

const CollectionGenerationLoader: React.FC<CollectionGenerationLoaderProps> = ({
  isGenerating,
  isDarkMode,
  onComplete,
}) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [completedStages, setCompletedStages] = useState<number[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const stageTimerRef = useRef<number | null>(null);

  // Handle stage progression - cycle through 3 stages
  useEffect(() => {
    if (!isGenerating) {
      setCurrentStage(0);
      setCompletedStages([]);
      setElapsedTime(0);
      return;
    }

    stageTimerRef.current = setInterval(() => {
      setElapsedTime((prev) => {
        const newTime = prev + 1;

        // Calculate which stage we should be on (all 3 stages)
        let totalDuration = 0;
        let newStage = 0;

        for (let i = 0; i < STAGES.length; i++) {
          totalDuration += STAGES[i].duration;
          if (newTime <= totalDuration) {
            newStage = i;
            break;
          }
        }

        // If we've gone through all stages, stay on last stage
        // (keep showing "Crafting Your Collection" while waiting)
        if (newTime > totalDuration) {
          newStage = STAGES.length - 1; // Stay on last stage (stage 2)
        }

        // Update current stage if changed
        if (newStage !== currentStage) {
          setCurrentStage(newStage);
          // Mark previous stage as completed
          if (newStage > 0 && !completedStages.includes(newStage - 1)) {
            setCompletedStages((prev) => [...prev, newStage - 1]);
          }
        }

        return newTime;
      });
    }, 1000);

    return () => {
      if (stageTimerRef.current) {
        clearInterval(stageTimerRef.current);
      }
    };
  }, [isGenerating, currentStage, completedStages]);

  // Loader only shows while generating
  if (!isGenerating) return null;

  console.log(
    "🎨 CollectionGenerationLoader is rendering! Stage:",
    currentStage
  );

  return (
    <div className="w-full max-w-2xl mx-auto py-8 px-6">
      {/* Header Banner */}
      <div
        className={`mb-6 p-3 rounded-lg text-center ${
          isDarkMode
            ? "bg-purple-500/20 text-purple-300"
            : "bg-purple-100 text-purple-700"
        }`}
      >
        <h2 className="text-lg font-bold">✨ Generating Collections</h2>
        <p className="text-xs mt-1">
          Please wait while we create something amazing...
        </p>
      </div>
      <div className="space-y-0 mt-20">
        {STAGES.map((stage, index) => {
          const isCompleted = completedStages.includes(index);
          const isActive = currentStage === index;

          return (
            <div key={stage.id} className="relative">
              {/* Timeline Item */}
              <div className="flex items-start gap-4">
                {/* Left: Timeline with Animation */}
                <div className="flex flex-col items-center">
                  {/* Lottie Animation or Check Icon */}
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{
                      scale: isActive || isCompleted ? 1 : 0.8,
                      opacity: isActive || isCompleted ? 1 : 0.4,
                    }}
                    transition={{ duration: 0.5 }}
                    className={`relative w-16 h-16 flex items-center justify-center rounded-full ${
                      isCompleted
                        ? ""
                        : isActive
                        ? isDarkMode
                          ? "bg-purple-500/20"
                          : "bg-purple-100"
                        : isDarkMode
                        ? "bg-gray-700/20"
                        : "bg-gray-100"
                    }`}
                  >
                    {isCompleted ? (
                      <div className="w-18 h-18 flex items-center justify-center">
                        <DotLottieReact
                          src="/assets/lottie_files/Success.lottie"
                          loop={false}
                          autoplay
                          style={{ width: "60px", height: "60px" }}
                        />
                      </div>
                    ) : (
                      <div className="w-18 h-18 flex items-center justify-center">
                        {isActive ? (
                          <DotLottieReact
                            src={stage.lottieFile}
                            loop
                            autoplay
                            style={{ width: "60px", height: "60px" }}
                          />
                        ) : (
                          <div className="animate-pulse rounded-full h-12 w-12 border-3 border-gray-400" />
                        )}
                      </div>
                    )}
                  </motion.div>

                  {/* Connecting Line */}
                  {index < STAGES.length - 1 && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{
                        height: isCompleted ? "100%" : isActive ? "50%" : 0,
                      }}
                      transition={{ duration: 0.5 }}
                      className={`w-1 flex-1 min-h-[50px] ${
                        isCompleted
                          ? isDarkMode
                            ? "bg-gradient-to-b from-green-500 to-purple-500"
                            : "bg-gradient-to-b from-green-600 to-purple-600"
                          : isActive
                          ? isDarkMode
                            ? "bg-gradient-to-b from-purple-500 to-gray-600"
                            : "bg-gradient-to-b from-purple-600 to-gray-300"
                          : isDarkMode
                          ? "bg-gray-700"
                          : "bg-gray-300"
                      }`}
                    />
                  )}
                </div>

                {/* Right: Content */}
                <div className="flex-1 pb-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`${stage.id}-${isActive}`}
                      initial={{ x: 20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`${
                        isActive || isCompleted ? "opacity-100" : "opacity-40"
                      }`}
                    >
                      {/* Title */}
                      <h3
                        className={`text-base font-bold mb-1 ${
                          isCompleted
                            ? isDarkMode
                              ? "text-green-400"
                              : "text-green-600"
                            : isActive
                            ? isDarkMode
                              ? "text-purple-400"
                              : "text-purple-600"
                            : isDarkMode
                            ? "text-gray-500"
                            : "text-gray-400"
                        }`}
                      >
                        {stage.title}
                      </h3>

                      {/* Description */}
                      <p
                        className={`text-sm ${
                          isActive || isCompleted
                            ? isDarkMode
                              ? "text-gray-300"
                              : "text-gray-700"
                            : isDarkMode
                            ? "text-gray-600"
                            : "text-gray-400"
                        }`}
                      >
                        {stage.description}
                      </p>

                      {/* Progress Bar for Active Stage */}
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="mt-4"
                        >
                          <div
                            className={`h-2 rounded-full overflow-hidden ${
                              isDarkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          >
                            <motion.div
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                              initial={{ width: "0%" }}
                              animate={{ width: "100%" }}
                              transition={{
                                duration: stage.duration,
                                ease: "linear",
                              }}
                            />
                          </div>
                          <p
                            className={`text-xs mt-2 ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Processing...
                          </p>
                        </motion.div>
                      )}

                      {/* Completed Badge */}
                      {isCompleted && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2 }}
                          className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-600 dark:text-green-400"
                        >
                          <MdCheck className="w-3 h-3" />
                          Completed
                        </motion.div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CollectionGenerationLoader;
